/** Where this install points, and the credential it holds.
 *
 *  Stored as a LIST with an active pointer even though there is exactly one
 *  today. The hosted architecture runs one single-tenant `bujo-api` process per
 *  workspace behind a gateway, and the CLI already models named local and hosted
 *  profiles. When a second destination arrives, this shape already holds it —
 *  and every cache key is already namespaced by destination id, so two
 *  destinations cannot collide or leak journal content into each other.
 *
 *  Retrofitting either of those later would mean an on-device migration, which
 *  cannot be run server-side and may never reach a stale install.
 *
 *  Browser storage is weaker than the Keychain the CLI uses. There is no
 *  equivalent here; the mitigations are a scoped token rather than the master
 *  token, and never putting the token in a URL or a log.
 */

export interface StoredToken {
  value: string;
  /** Hosted tokens are audience-bound and refreshable; local ones are not. */
  expiresAt?: string;
  refreshable?: boolean;
}

export interface Destination {
  id: string;
  label: string;
  /** Empty string means same-origin, which is the deployed shape. */
  baseUrl: string;
  token: StoredToken;
}

interface DestinationsFile {
  version: 1;
  activeId: string | null;
  destinations: Destination[];
}

const STORAGE_KEY = 'bujo.destinations.v1';

const EMPTY: DestinationsFile = { version: 1, activeId: null, destinations: [] };

function read(): DestinationsFile {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as DestinationsFile;
    if (parsed.version !== 1 || !Array.isArray(parsed.destinations)) return EMPTY;
    return parsed;
  } catch {
    return EMPTY;
  }
}

function write(file: DestinationsFile): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(file));
}

export function activeDestination(): Destination | null {
  const file = read();
  return file.destinations.find((d) => d.id === file.activeId) ?? null;
}

export function saveDestination(input: {
  label: string;
  baseUrl: string;
  token: string;
}): Destination {
  const file = read();
  const destination: Destination = {
    // Derived from the base URL so the same server reuses its cache namespace
    // across re-provisioning rather than orphaning it.
    id: destinationId(input.baseUrl),
    label: input.label,
    baseUrl: input.baseUrl,
    token: { value: input.token },
  };
  const others = file.destinations.filter((d) => d.id !== destination.id);
  write({ version: 1, activeId: destination.id, destinations: [...others, destination] });
  return destination;
}

/** A destination saved without a credential.
 *
 *  `Continue offline · sync later` produces one: the app knows where the journal
 *  lives and cannot read it yet. Captures queue against it — the queue is keyed
 *  by destination and never needs a token to enqueue — and drain once a token
 *  arrives. Named rather than left as an empty-string check, because every
 *  screen has to tolerate this state and a bare `!token` reads like a bug.
 */
export function needsToken(destination: Destination | null): boolean {
  return Boolean(destination) && !destination!.token.value.trim();
}

export function clearActive(): void {
  const file = read();
  write({ ...file, activeId: null });
}

function destinationId(baseUrl: string): string {
  const normalized = baseUrl.replace(/\/+$/, '') || 'same-origin';
  let hash = 0;
  for (let i = 0; i < normalized.length; i += 1) {
    hash = (hash * 31 + normalized.charCodeAt(i)) | 0;
  }
  return `d${Math.abs(hash).toString(36)}`;
}

/** Namespace for anything persisted on device. Every cache store, queued
 *  operation and service-worker cache name goes through this. */
export function scopedKey(destination: Destination, key: string): string {
  return `bujo:${destination.id}:${key}`;
}
